import React, { useEffect, useState } from 'react';
import './portfolio.css';
import { FaArrowRight, FaPause, FaPlay, FaRegCircleDot } from 'react-icons/fa6';
import portfolioConfig from '../../config/portfolioConfig';

const Portfolio = () => {
  const { projects } = portfolioConfig;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!isPlaying) {
      return undefined;
    }

    const reel = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % projects.length);
    }, 4500);

    return () => window.clearInterval(reel);
  }, [isPlaying, projects.length]);

  const activeProject = projects[activeIndex];

  const activateProject = (index) => {
    setActiveIndex(index);
    setIsPlaying(false);
  };

  return (
    <section id="portfolio" className="work-section section">
      <div className="container work-section__container">
        <div className="work-section__intro">
          <div>
            <p className="section-eyebrow">Selected Work</p>
            <h2>Current links, real status notes, and a more grounded way to present them.</h2>
          </div>
          <p className="section-copy">
            The old portfolio section was hidden and the terminal still referenced outdated demo
            projects. This rebuild makes the current work visible again and adds a playable project
            reel so the section feels active without falling back on generic effects.
          </p>
        </div>

        <div className="work-reel panel">
          <div className={`work-reel__visual work-reel__visual--${activeProject.tone}`}>
            {activeProject.visual ? (
              <img src={activeProject.visual} alt={activeProject.title} className="work-reel__image" />
            ) : (
              <div className="work-reel__placeholder">
                <span className="work-reel__placeholder-index">{activeProject.index}</span>
                <strong>{activeProject.title}</strong>
                <p>{activeProject.status}</p>
              </div>
            )}
            <span className={`work-reel__status work-reel__status--${activeProject.status.toLowerCase().includes('live') ? 'live' : 'issue'}`}>
              {activeProject.status}
            </span>
          </div>

          <div className="work-reel__content">
            <div className="work-reel__topline">
              <p className="section-eyebrow">Project reel</p>
              <button
                type="button"
                className="work-reel__play"
                onClick={() => setIsPlaying((playing) => !playing)}
              >
                {isPlaying ? <FaPause /> : <FaPlay />}
                {isPlaying ? 'Pause reel' : 'Play reel'}
              </button>
            </div>

            <div className="work-reel__headline">
              <span>{activeProject.index}</span>
              <div>
                <h3>{activeProject.title}</h3>
                <p>{activeProject.label}</p>
              </div>
            </div>

            <p className="work-reel__summary">{activeProject.summary}</p>
            <p>{activeProject.detail}</p>

            <div className="work-reel__notes">
              <div className="panel">
                <span>Inspection note</span>
                <strong>{activeProject.inspectionNote}</strong>
              </div>
              <div className="panel">
                <span>Technologies</span>
                <strong>{activeProject.technologies.join(' • ')}</strong>
              </div>
            </div>

            <div className="work-reel__actions">
              <a href={activeProject.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                Visit project <FaArrowRight />
              </a>
            </div>

            <div className="work-reel__progress" aria-label="Project reel progress">
              {projects.map((project, index) => (
                <button
                  key={project.id}
                  type="button"
                  className={index === activeIndex ? 'active' : ''}
                  onClick={() => activateProject(index)}
                  aria-label={`Show ${project.title}`}
                >
                  <FaRegCircleDot />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="work-grid">
          {projects.map((project, index) => (
            <article
              key={project.id}
              className={`work-card panel ${index === activeIndex ? 'active' : ''}`}
            >
              <button type="button" className="work-card__button" onClick={() => activateProject(index)}>
                <div className="work-card__header">
                  <span>{project.index}</span>
                  <p>{project.status}</p>
                </div>
                <h3>{project.title}</h3>
                <p className="work-card__label">{project.label}</p>
                <p>{project.summary}</p>
              </button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
